import type { MemberWithProfile, OrgRole, PlatformRole } from "./adminTypes";

type Params = {
  auth: any;
  effectiveOrgId: string | null | undefined;
  inviteEmail: string;
  inviteRole: OrgRole;
  invitePlatformRole: PlatformRole;
  mergeSourceUserId: string;
  mergeTargetUserId: string;
  refresh: () => Promise<void>;
  formatAdminError: (err: unknown) => string;
  setMsg: (value: string) => void;
  setLoading: (value: boolean) => void;
  setInviteMsg: (value: string) => void;
  setInviteLoading: (value: boolean) => void;
  setInviteEmail: (value: string) => void;
  setInvitePlatformRole: (value: PlatformRole) => void;
  setMergeBusy: (value: boolean) => void;
  setMergeSourceUserId: (value: string) => void;
  setMergeTargetUserId: (value: string) => void;
};

export function useAdminPanelAccountActions(params: Params) {
  async function deleteUser(userId: string, name: string) {
    if (!window.confirm(`PERMANENTLY DELETE "${name}"?\n\nThis removes them from auth entirely. They will need a new invite to rejoin. This cannot be undone.`)) return;
    const token = params.auth.session?.access_token;
    if (!token) { params.setMsg("Not authenticated."); return; }
    params.setMsg("");
    params.setLoading(true);
    try {
      const res = await fetch("/api/admin/action?action=delete-user", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      params.setMsg(`${name} permanently deleted.`);
      await params.refresh();
    } catch (e: any) {
      params.setMsg(e?.message || String(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function resendInvite(member: MemberWithProfile) {
    if (!params.effectiveOrgId) return;
    const token = params.auth.session?.access_token;
    if (!token) {
      params.setMsg("Not authenticated.");
      return;
    }
    const addr = (member.email ?? "").trim();
    if (!addr) {
      params.setMsg("No email on file for this member.");
      return;
    }
    if (!window.confirm(`Resend invite email to ${addr}?`)) return;
    params.setMsg("");
    params.setLoading(true);
    try {
      const res = await fetch("/api/admin/action?action=invite-resend", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: member.user_id, orgId: params.effectiveOrgId }),
      });
      const json = (await res.json()) as { error?: string; email?: string };
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      params.setMsg(`Invite resent to ${json.email ?? addr}.`);
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setLoading(false);
    }
  }

  async function submitInvite() {
    if (!params.effectiveOrgId || !params.inviteEmail.trim()) return;
    const token = params.auth.session?.access_token;
    if (!token) { params.setInviteMsg("Not authenticated."); return; }
    params.setInviteLoading(true);
    params.setInviteMsg("");
    try {
      const precheckRes = await fetch("/api/admin/action?action=invite-precheck", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: params.inviteEmail.trim(), orgId: params.effectiveOrgId }),
      });
      const precheckJson = (await precheckRes.json()) as {
        potentialMatches?: Array<{ email?: string | null; full_name?: string | null; display_name?: string | null }>;
        error?: string;
      };
      if (!precheckRes.ok) throw new Error(precheckJson.error ?? `HTTP ${precheckRes.status}`);
      const potentialMatches = precheckJson.potentialMatches ?? [];
      let allowPotentialDuplicate = false;
      if (potentialMatches.length > 0) {
        const labels = potentialMatches.map((m) => String(m.full_name || m.display_name || m.email || "Unknown member")).join(", ");
        allowPotentialDuplicate = window.confirm(`Potential duplicate invite detected for: ${labels}. Continue anyway and create a new user record?`);
        if (!allowPotentialDuplicate) {
          params.setInviteMsg("Invite cancelled to avoid duplicate user creation.");
          return;
        }
      }

      const res = await fetch("/api/admin/action?action=invite", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email: params.inviteEmail.trim(),
          orgId: params.effectiveOrgId,
          role: params.inviteRole,
          allowPotentialDuplicate,
          platformRole: params.invitePlatformRole || undefined,
        }),
      });
      const json = (await res.json()) as {
        error?: string;
        linked_existing_exact_email_user?: boolean;
        pending_invite_aliased_to_user_id?: string | null;
        migrated_contact_rows?: number;
      };
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      const inviteAddr = params.inviteEmail.trim();
      if (json.linked_existing_exact_email_user) {
        const migratedCount = Number(json.migrated_contact_rows ?? 0);
        params.setInviteMsg(migratedCount > 0 ? `Existing user ${inviteAddr} added to this org (no new auth user created). Contact routing inherited for ${migratedCount} org row${migratedCount === 1 ? "" : "s"}.` : `Existing user ${inviteAddr} added to this org (no new auth user created).`);
      } else if (json.pending_invite_aliased_to_user_id) {
        params.setInviteMsg(`Invite sent to ${inviteAddr}. Pending invite alias was linked to an accepted org user to avoid duplicate accounts.`);
      } else {
        params.setInviteMsg(`Invite sent to ${inviteAddr}.`);
      }
      params.setInviteEmail("");
      params.setInvitePlatformRole("");
      await params.refresh();
    } catch (e: unknown) {
      params.setInviteMsg(params.formatAdminError(e));
    } finally {
      params.setInviteLoading(false);
    }
  }

  async function mergeDuplicateUsers() {
    if (!params.effectiveOrgId) return;
    const token = params.auth.session?.access_token;
    if (!token) {
      params.setMsg("Not authenticated.");
      return;
    }
    if (!params.mergeSourceUserId || !params.mergeTargetUserId) {
      params.setMsg("Pick both source and target users to merge.");
      return;
    }
    if (params.mergeSourceUserId === params.mergeTargetUserId) {
      params.setMsg("Source and target users must be different.");
      return;
    }
    if (!window.confirm("Merge source user into target user? Source account will be deactivated/removed.")) return;
    params.setMergeBusy(true);
    params.setMsg("");
    try {
      const res = await fetch("/api/admin/action?action=merge-duplicate-users", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sourceUserId: params.mergeSourceUserId,
          targetUserId: params.mergeTargetUserId,
          orgId: params.effectiveOrgId,
          keepSourceAsInactiveProfile: true,
        }),
      });
      const json = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      params.setMsg("Users merged. Source was kept as inactive profile.");
      params.setMergeSourceUserId("");
      params.setMergeTargetUserId("");
      await params.refresh();
    } catch (e: unknown) {
      params.setMsg(params.formatAdminError(e));
    } finally {
      params.setMergeBusy(false);
    }
  }

  return { deleteUser, resendInvite, submitInvite, mergeDuplicateUsers };
}
